import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Shield, Zap, Leaf, Settings, LogOut, User,
  ChevronDown, Menu, X, AlertTriangle, HelpCircle, Activity, Sprout, Recycle, 
} from 'lucide-react';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { useTenantLogo } from '../hooks/useTenantLogo';
import { usePermissions } from '../context/PermissionContext';
import NotificationBell from './NotificationBell';
import SiteSelector from './SiteSelector';
import { formatRoleName } from '../utils/roleUtils';

const TabNavigation = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  // Module access control
  const { isModuleEnabled, loading } = useModuleAccess();
  const { isAdmin } = usePermissions();
  // Same role predicate AdminManagement.js uses to admit a user to /admin.
  const canOpenAdminSettings = ['admin', 'Admin', 'hse_manager', 'HSE Manager'].includes(user?.role);

  // Tenant logo (data URL via the tenant-scoped logo-stream proxy);
  // null when the tenant hasn't uploaded one — the header then shows
  // just the platform logo + company name as before.
  const tenantLogo = useTenantLogo();

  const getInitials = (name) => {
    if (!name) return null;
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return null;
    const first = parts[0][0];
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
  };
  const initials = getInitials(user?.name);

  const allTabs = [
    { id: 'safety', label: 'Safety', icon: Shield, path: '/safety', moduleSlug: 'safety' },
    { id: 'energy', label: 'ERS', icon: Zap, path: '/energy-resources', moduleSlug: 'energy' },
    { id: 'sustainability', label: 'Sustainability', icon: Leaf, path: '/sustainability', moduleSlug: 'sustainability' },
    { id: 'boardpack', label: 'Governance', icon: AlertTriangle, path: '/board-pack', moduleSlug: 'board' },
    { id: 'oim', label: 'OIM', icon: Activity, path: '/online-intelligence', moduleSlug: 'oim' },
    { id: 'mrv', label: 'MRV', icon: Sprout, path: '/mrv', moduleSlug: 'mrv' },
    { id: 'lca', label: 'LCA', icon: Recycle, path: '/lca', moduleSlug: 'lca' },
    { id: 'admin', label: 'Admin', icon: Settings, path: '/admin', moduleSlug: 'admin', adminOnly: true },
  ];

  // Filter tabs based on enabled modules and admin access
  const tabs = allTabs.filter(tab => {
    if (tab.adminOnly) return isAdmin();
    return isModuleEnabled(tab.moduleSlug);
  });

  // Tab labels are hidden on narrower screens only to keep a FULL tab set
  // from overflowing (the 1280-1366px overlap fix). When a tenant has few
  // enabled modules there is plenty of room, so show labels earlier —
  // otherwise a single enabled module renders as a bare icon with no text.
  // overflow-x-auto on the tab strip remains the safety net either way.
  const tabLabelClass =
    tabs.length <= 3 ? 'inline'
    : tabs.length <= 5 ? 'hidden lg:inline'
    : 'hidden xl:inline';

  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/safety') || path.includes('/hira') || path.includes('/ptw') ||
        path.includes('/capa') || path.includes('/observations') || path.includes('/audits') ||
        path.includes('/training') || path.includes('/meetings') || path.includes('/ssem') ||
        path.includes('/documents')) {
      return 'safety';
    }
    if (path.includes('/energy') || path.includes('/resources') || path.includes('/ers')) return 'energy';
    if (path.includes('/lca')) return 'lca';
    if (path.includes('/sustainability')) return 'sustainability';
    if (path.includes('/board-pack') || path.includes('/risk')) return 'boardpack';
    if (path.includes('/online-intelligence') || path.includes('/oim')) return 'oim';
    if (path.includes('/mrv')) return 'mrv';
    if (path.includes('/admin')) return 'admin';
    return 'dashboard';
  };

  const activeTab = getActiveTab();

  const handleTabClick = (tab) => {
    navigate(tab.path);
    setMobileMenuOpen(false);
  };

  return (
    <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
      <div className="max-w-full mx-auto px-3 sm:px-4 lg:px-6">
        <div className="flex items-center justify-between h-16 gap-2 lg:gap-3">
          {/* Logo and Brand */}
          <div className="flex items-center flex-shrink-0 min-w-fit">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
            >
              <img
                src="/ergoniq-logo.svg"
                alt="Ergoniq Logo"
                className="h-10 w-auto"
              />
              {/* Company block (tenant logo + name) — hidden below lg so
                  icon-only nav has room. Below lg the company info still
                  appears inside the profile dropdown via user.company_name. */}
              <div className="hidden lg:flex items-center gap-2 border-l-2 border-gray-300 pl-2 ml-1">
                {tenantLogo && (
                  <img
                    src={tenantLogo}
                    alt={`${user?.company_name || 'Company'} logo`}
                    className="h-8 w-auto max-w-[120px] object-contain"
                    data-testid="tenant-logo"
                  />
                )}
                <div>
                  <div className="text-xs text-gray-600 truncate max-w-[140px]">{user?.company_name || 'Company'}</div>
                </div>
              </div>
            </button>
          </div>

          {/* Desktop Navigation Tabs — label visibility scales with how many
              tabs are enabled (see tabLabelClass above): few tabs always show
              labels, a full set goes icon-only below xl. overflow-x-auto is
              the safety net if even icons don't fit on very cramped widths
              (rare, but avoids the overlap-on-neighbour behaviour we hit at
              1280-1366px before this fix). */}
          <div className="hidden md:flex items-center gap-0.5 flex-1 justify-center min-w-0 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab)}
                  title={tab.label}
                  className={`flex items-center gap-1.5 px-2 py-2 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap ${
                    isActive
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className={tabLabelClass}>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {/* Site Selector */}
            <SiteSelector />

            {/* Help Button */}
            <button
              onClick={() => window.open('/help', '_blank', 'noopener,noreferrer')}
              className="relative p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Help & Documentation"
            >
              <HelpCircle className="w-5 h-5" />
            </button>

            {/* Notifications */}
            <NotificationBell />

            {/* Profile Dropdown — name+role visible only at xl+ to keep the bar fitting */}
            <div className="relative">
              <button
                onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                className="flex items-center gap-1.5 px-2 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title={user?.name ? `${user.name}${user.role ? ' · ' + formatRoleName(user.role) : ''}` : 'Profile'}
              >
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center shrink-0">
                  {initials ? (
                    <span className="text-xs font-bold text-white">{initials}</span>
                  ) : (
                    <User className="w-5 h-5 text-white" />
                  )}
                </div>
                <div className="hidden 2xl:block text-left max-w-[140px]">
                  <div className="text-sm font-semibold truncate">{user?.name}</div>
                  <div className="text-xs text-gray-600 truncate">{formatRoleName(user?.role)}</div>
                </div>
                <ChevronDown className="w-4 h-4 shrink-0" />
              </button>

              {/* Dropdown Menu */}
              {profileDropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  {/* User header — name, email/role, large avatar with initials */}
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center shrink-0">
                      {initials ? (
                        <span className="text-sm font-bold text-white">{initials}</span>
                      ) : (
                        <User className="w-6 h-6 text-white" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate">
                        {user?.name || 'User'}
                      </div>
                      <div className="text-xs text-gray-600 truncate">
                        {user?.email || user?.role || ''}
                      </div>
                      {user?.email && user?.role && (
                        <div className="text-[11px] text-gray-500 truncate">{formatRoleName(user.role)}</div>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      window.open('/help', '_blank', 'noopener,noreferrer');
                      setProfileDropdownOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <HelpCircle className="w-4 h-4" />
                    <span>Help & Documentation</span>
                  </button>
                  {/* CMP-20: Settings opens /admin, which only admits Admin and
                      HSE Manager (AdminManagement.js). Hide it for everyone
                      else instead of leading to a "no permission" dead end. */}
                  {canOpenAdminSettings && (
                    <button
                      onClick={() => {
                        navigate('/admin');
                        setProfileDropdownOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                    >
                      <Settings className="w-4 h-4" />
                      <span>Settings</span>
                    </button>
                  )}
                  <hr className="my-1" />
                  <button
                    onClick={() => {
                      setProfileDropdownOpen(false);
                      onLogout();
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            {/* Mobile Site Selector */}
            <div className="px-4 pb-3">
              <SiteSelector />
            </div>
            <div className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabClick(tab)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-semibold transition-colors ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}

              {/* Help Link in Mobile Menu */}
              <button
                onClick={() => {
                  window.open('/help', '_blank', 'noopener,noreferrer');
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <HelpCircle className="w-5 h-5" />
                <span>Help & Documentation</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Close dropdown when clicking outside */}
      {profileDropdownOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setProfileDropdownOpen(false)}
        />
      )}
    </div>
  );
};

export default TabNavigation;
